// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 52 — Brand Consistency Scoring
// ============================================================================

/** A dimension of brand consistency an asset is scored against. */
export type DJSEMBrandScoreDimension =
  | "color" | "typography" | "composition" | "mood" | "genre_fit"
  | "release_fit" | "platform_fit" | "visual_safety" | "rights_provenance";

/** The overall verdict for an asset's brand consistency scoring. */
export type DJSEMBrandScoreVerdict = "strong_match" | "usable" | "needs_review" | "off_brand" | "blocked";

/** A single dimension's score, explanation, and supporting evidence within a brand consistency scoring. */
export interface DJSEMBrandConsistencyDimensionScore {
  dimension: DJSEMBrandScoreDimension;
  score: number;
  explanation: string;
  evidence: string[];
  warnings: string[];
}

/** The full brand consistency scoring result for a single asset against a Brand World. */
export interface DJSEMBrandConsistencyScore {
  id: string;
  assetId: string;
  brandWorldId: string;
  overallScore: number;
  verdict: DJSEMBrandScoreVerdict;
  dimensions: DJSEMBrandConsistencyDimensionScore[];
  rightsWarnings: string[];
  suggestedFixes: string[];
  reviewStatus: "not_reviewed" | "approved" | "rejected" | "changes_requested";
  createdAt: string;
}

/** Input required to score an asset's consistency against a Brand World. */
export interface BrandScoringInput {
  assetId: string;
  brandWorldId: string;
  brandColors: string[];
  brandMotifs: string[];
  restrictedElements: string[];
  assetColors: string[];
  assetMotifs: string[];
  hasRightsIssue: boolean;
}

/** Scores visual assets for consistency with a project's Brand World and manages review status. */
export class BrandConsistencyScoringService {
  /**
   * Scores an asset's color/motif match against a Brand World, checks for restricted elements
   * and rights issues, and derives an overall verdict and suggested fixes.
   * @param input - the asset's colors/motifs plus the Brand World's palette, motifs, and restricted elements.
   * @returns the brand consistency score, including per-dimension scores and a verdict.
   */
  scoreAsset(input: BrandScoringInput): DJSEMBrandConsistencyScore {
    const dims: DJSEMBrandConsistencyDimensionScore[] = [];
    // Color match
    const colorMatches = input.assetColors.filter(c => input.brandColors.includes(c)).length;
    const colorScore = input.brandColors.length > 0 ? colorMatches / input.brandColors.length : 0.5;
    dims.push({ dimension: "color", score: colorScore, explanation: `${colorMatches}/${input.brandColors.length} brand colors matched`, evidence: input.assetColors, warnings: [] });
    // Motif match
    const motifMatches = input.assetMotifs.filter(m => input.brandMotifs.includes(m)).length;
    const motifScore = input.brandMotifs.length > 0 ? motifMatches / input.brandMotifs.length : 0.5;
    dims.push({ dimension: "mood", score: motifScore, explanation: `${motifMatches}/${input.brandMotifs.length} brand motifs matched`, evidence: input.assetMotifs, warnings: [] });
    // Restricted elements
    const violations = input.assetMotifs.filter(m => input.restrictedElements.includes(m));
    if (violations.length > 0) dims.push({ dimension: "visual_safety", score: 0, explanation: `Restricted element(s): ${violations.join(", ")}`, evidence: violations, warnings: ["Restricted brand element violated"] });
    // Rights
    const rightsScore = input.hasRightsIssue ? 0 : 1;
    dims.push({ dimension: "rights_provenance", score: rightsScore, explanation: input.hasRightsIssue ? "Rights issue present" : "Rights clear", evidence: [], warnings: input.hasRightsIssue ? ["Rights issue blocks approval"] : [] });

    const overall = dims.reduce((sum, d) => sum + d.score, 0) / dims.length;
    const blocked = input.hasRightsIssue || violations.length > 0;
    const verdict: DJSEMBrandScoreVerdict = blocked ? "blocked" : overall >= 0.8 ? "strong_match" : overall >= 0.5 ? "usable" : overall >= 0.3 ? "needs_review" : "off_brand";
    const fixes: string[] = [];
    if (colorScore < 0.5) fixes.push("Adjust colors to match Brand World palette");
    if (motifScore < 0.5) fixes.push("Include more Brand World motifs");
    if (violations.length > 0) fixes.push("Remove restricted elements");

    return { id: `bcs_${_seq.next()}`, assetId: input.assetId, brandWorldId: input.brandWorldId, overallScore: overall, verdict, dimensions: dims, rightsWarnings: input.hasRightsIssue ? ["Rights issue present"] : [], suggestedFixes: fixes, reviewStatus: "not_reviewed", createdAt: AT };
  }

  /**
   * Marks a score as approved, unless its verdict is `"blocked"`.
   * @param score - the score to approve.
   * @returns the score unchanged if `verdict` is `"blocked"`, otherwise a copy with `reviewStatus: "approved"`.
   */
  approveScore(score: DJSEMBrandConsistencyScore): DJSEMBrandConsistencyScore {
    if (score.verdict === "blocked") return score;
    return { ...score, reviewStatus: "approved" };
  }

  /**
   * Marks a score as rejected.
   * @param score - the score to reject.
   * @returns a copy of the score with `reviewStatus: "rejected"`.
   */
  rejectScore(score: DJSEMBrandConsistencyScore): DJSEMBrandConsistencyScore {
    return { ...score, reviewStatus: "rejected" };
  }
}
